<template>
  <aside class="main-sidebar sidebar-dark-primary elevation-4">
    <!-- Brand Logo -->
    <router-link to="/">
      <a class="brand-link">
        <img
          src="@/assets/logo_White.png"
          alt="AdminLTE Logo"
          class="brand-image img-circle elevation-3"
          style="opacity: .8"
        />
        <span class="brand-text font-weight-light">CONNEXUS</span>
      </a>
    </router-link>

    <!-- Sidebar -->
    <div class="sidebar">
      <!-- Sidebar user panel -->
      <div class="user-panel mt-3 pb-3 mb-3 d-flex">
        <div class="image d-flex align-items-center">
          <i :class="'fas fa-user-circle fa-2x ' + colorIcon"></i>
        </div>
        <div class="info">
          <a href="#" class="d-block">{{ storeAut.getEmail }}</a>
        </div>
      </div>

      <div class="form-inline">
        <div class="input-group" data-widget="sidebar-search">
          <input
            v-model="searchQuery"
            class="form-control form-control-sidebar"
            type="search"
            placeholder="Buscar menú o módulo"
            aria-label="Search"
          />
        </div>
      </div>

      <nav class="mt-2 mb-5" id="primary_nav_wrap">
        <ul
          v-if="!searchQuery"
          class="nav nav-pills nav-sidebar nav-treeview flex-column"
          data-widget="treeview"
          role="menu"
          data-accordion="false"
        >
          <template v-for="(menu, index) in storeMenu.getClassApps" :key="index">
            <template v-if="getSubmenus(menu).length > 0">
              <li
                class="nav-item"
                :key="menu.id"
                @click="toggleMenu(index, menu.status)"
              >
                <a href="#" class="nav-link">
                  <i :class="menu.icon"></i>
                  <p>{{ menu.name }}</p>
                </a>
                <ul
                  class="nav nav-treeview"
                  :style="{ display: menu.status ? 'block' : 'none' }"
                >
                  <li
                    class="nav-item"
                    v-for="submenu in getSubmenus(menu)"
                    :key="submenu.moduleId"
                  >
                    <router-link
                      class="nav-link"
                      :to="{ name: submenu.Componet }"
                    >
                      <i class="far fa-circle nav-icon" style="font-size: 6px"></i>
                      <p>{{ submenu.name }}</p>
                    </router-link>
                  </li>
                </ul>
              </li>
            </template>
            <li class="nav-item" v-else-if="menu.public === true">
              <router-link class="nav-link" :to="{ name: menu.nameComponet }">
                <i :class="menu.icon"></i>
                <p>{{ menu.name }}</p>
              </router-link>
            </li>

            <li
              class="nav-item"
              v-else-if="menu.public === false && storeAut.checkAcctionPermissions(menu.id, menu.idMod, ['Visible'])"
            >
              <router-link class="nav-link" :to="{ name: menu.nameComponet }">
                <i :class="menu.icon"></i>
                <p>{{ menu.name }}</p>
              </router-link>
            </li>
          </template>
        </ul>

        <ul
          v-else-if="filteredMenus.length > 0"
          class="nav nav-pills nav-sidebar flex-column mb-3"
        >        
          <li 
            v-for="item in filteredMenus.filter(item => item.type === 'menu')" 
            :key="item.id" 
            class="nav-item"
          >
            <router-link
              class="nav-link"
              :to="{ name: item.nameComponet }"
              @click="clearSearch"
            >
              <i :class="item.icon"></i>
              <p v-html="highlight(item.name)"></p>
            </router-link>
          </li>
          
          <!-- Submenús con redirect -->
          <li 
            v-for="item in filteredMenus.filter(item => item.type === 'submenu')" 
            :key="item.moduleId" 
            class="nav-item"
          >
            <router-link
              class="nav-link"
              :to="{ name: item.Componet }"
              @click="clearSearch"
            >
              <i class="far fa-circle nav-icon" style="font-size: 6px"></i>
              <p>
                <span v-html="highlight(item.name)"></span>
                <small class="text-muted d-block" style="font-size: 0.75rem;">
                </small>
              </p>
            </router-link>
          </li>
        </ul>

        <p v-else-if="searchQuery && !isLoading" class="text-center text-muted mt-3">
          Sin coincidencias
        </p>
      </nav>
    </div>
  </aside>
</template>

<script lang="ts" setup>
import { ref, watch, computed } from 'vue'
import { useMenuStore } from '@/stores/MenuStore'
import { useAuthorization } from '@/stores/AuthorizationStore'

const storeMenu = useMenuStore()
const storeAut = useAuthorization()
const colorIcon = import.meta.env.VITE_ICON_COLOR

const searchQuery = ref('')
const filteredMenus = ref<any[]>([])
const isLoading = ref(false)

type Submenu = {
  name: string
  icon: string
  Componet: string
  moduleId: string
  parentMenuName?: string
  hasRedirect: boolean
  search: boolean
}

const modulesApp = (id: string, group: string): Submenu[] => {
  const dto = storeMenu.getClassModules
  return Object.values(dto)
    .filter(
      (item) =>
        item.idAplication === id &&
        item.group === group &&
        storeAut.checkAcctionPermissions(id, item.id, ['Visible'])
    )
    .map((item) => ({
      name: item.name ?? '',
      icon: item.icon ?? '',
      Componet: item.nameComponet ?? '',
      moduleId: item.id ?? '',
      hasRedirect: !!item.nameComponet, // Si tiene componente, tiene redirect
      search: item.search ?? true // Por defecto true si no está definido
    }))
}

const getSubmenus = (menu: any): Submenu[] => modulesApp(menu.id, menu.group)

const toggleMenu = (index: number | string, status: boolean) => {
  storeMenu.getClassApps[index].status = !status
}

const getAllMenuStructure = computed(() => {
  const allMenus = Object.values(storeMenu.getClassApps)
  
  return allMenus.map(menu => {
    const submenus = getSubmenus(menu)
    return {
      ...menu,
      type: 'menu',
      hasRedirect: !!menu.nameComponet, // Menú tiene redirect si tiene componente
      search: menu.search ?? true, // Por defecto true si no está definido
      submenus: submenus.map(submenu => ({
        ...submenu,
        type: 'submenu',
        parentMenuName: menu.name,
        parentMenuId: menu.id
      }))
    }
  })
})

let searchTimeout: number | null = null
watch(searchQuery, (newValue) => {
  if (searchTimeout) clearTimeout(searchTimeout)
  
  if (newValue.trim()) {
    isLoading.value = true
  }
  
  searchTimeout = window.setTimeout(() => {
    filterMenus(newValue)
    isLoading.value = false
  }, 200)
})

const filterMenus = (query: string) => {
  const q = query.toLowerCase().trim()
  if (!q) {
    filteredMenus.value = []
    return
  }

  const menuStructure = getAllMenuStructure.value
  const results: any[] = []

  menuStructure.forEach(menu => {
    if (menu.nameComponet && menu.search) {
      const menuName = menu.name?.toLowerCase() || ''
      const menuComponent = menu.nameComponet?.toLowerCase() || ''
      
      if (menuName.includes(q) || menuComponent.includes(q)) {
        results.push({
          ...menu,
          type: 'menu',
          displayName: menu.name,
          parentMenuName: null
        })
      }
    }

    menu.submenus.forEach((submenu: any) => {
      const submenuName = submenu.name?.toLowerCase() || ''
      const submenuComponent = submenu.Componet?.toLowerCase() || ''
      
      if (submenu.Componet && submenu.search && (submenuName.includes(q) || submenuComponent.includes(q))) {
        results.push({
          ...submenu,
          type: 'submenu',
          displayName: submenu.name,
          parentMenuName: menu.name
        })
      }
    })
  })

  filteredMenus.value = results
}

const clearSearch = () => {
  searchQuery.value = ''
  filteredMenus.value = []
  isLoading.value = false
}

const highlight = (text: string) => {
  if (!searchQuery.value.trim() || !text) return text
  const regex = new RegExp(`(${searchQuery.value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
  return text.replace(regex, '<mark>$1</mark>')
}
</script>


<style>
.nav-item .router-link-active {
  background-color: #17a589;
}
.main-sidebar {
  font-size: 1rem;
}

.user-panel .image {
  margin-right: 10px;
}

#primary_nav_wrap {
  margin-top: 1rem;
}

#primary_nav_wrap ul {
  list-style: none;
  margin: 0;
  padding: 0;
}

#primary_nav_wrap ul a {
  display: block;
  padding: 0.5rem 1rem;
}

.sidebar_show {
  display: block;
}

.form-inline {
  margin-bottom: 1rem;
  padding: 0 0.5rem;
}

.input-group {
  position: relative;
}


.form-control-sidebar {
  background-color: #2c2c2c;
  color: #fff;
}

.form-control-sidebar::placeholder {
  color: #b5b5b5;
}

.btn-sidebar {
  background-color: #2c2c2c;
  color: #fff;
}

.btn-sidebar:hover {
  background-color: #1c1c1c;
}

.search-loading {
  position: relative;
}

.search-loading::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 16px;
  height: 16px;
  margin: -8px 0 0 -8px;
  border: 2px solid transparent;
  border-top: 2px solid #fff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}


.search-suggestions {
  padding: 0.5rem 0;
  text-align: center;
}


.nav.nav-pills.nav-sidebar.flex-column.mb-3 {
  max-height: 60vh;
  overflow-y: auto;
  padding-right: 5px;
  animation: fadeIn 0.3s ease;
}



.nav.nav-pills.nav-sidebar .nav-link {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
}

.nav.nav-pills.nav-sidebar .nav-link:hover {
  background-color: rgba(23, 165, 137, 0.15);
  transform: translateX(3px);
}


.nav.nav-pills.nav-sidebar .nav-link:hover:before {
  transform: scaleY(1);
}

.nav.nav-pills.nav-sidebar .nav-link i {
  margin-right: 0.75rem;
  font-size: 1rem;
  width: 20px;
  text-align: center;
  color: #b5b5b5;
}

.nav.nav-pills.nav-sidebar .nav-link p {
  margin: 0;
  flex-grow: 1;
  font-size: 0.95rem;
}

.nav.nav-pills.nav-sidebar .nav-link small {
  font-size: 0.7rem;
  color: #aaa;
  margin-left: 0.5rem;
  background-color: rgba(255, 255, 255, 0.1);
  padding: 0.1rem 0.4rem;
  border-radius: 3px;
}

mark {
  background-color: #ffc107;
  color: #000;
  padding: 0.1rem 0.2rem;
  border-radius: 2px;
  font-weight: 600;
}

.text-center.text-muted.mt-3 {
  padding: 1.5rem;
  font-style: italic;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
  margin: 0 0.5rem;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>